/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users2, MapPin, CalendarDays, Tag, Phone, Trash2 } from 'lucide-react';
import { CATEGORY_META, timeAgo } from '@/lib/utils-cat';
import { toast } from 'sonner';
import ReportDialog from '@/components/ReportDialog';
import BookmarkButton from '@/components/BookmarkButton';
import { useAuth } from '@/lib/auth';
import { TypeBadge } from './TypeBadge';

const MetaRow = ({ item }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      {item.location && (<div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {item.location}</div>)}
      {(item.start_date || item.end_date) && (<div className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {item.start_date || '?'} → {item.end_date || '?'}</div>)}
      {item.deadline && (<div className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {t('deadline')}: {item.deadline}</div>)}
      {item.category && (<div className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> {t(item.category)}</div>)}
      {item.is_paid && item.price && (<div>· {item.price}</div>)}
      {item.contact_info && (<div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {item.contact_info}</div>)}
    </div>
  );
};

export const BoardItemCard = ({ item, onChanged, onDeleted }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [joining, setJoining] = useState(false);
  const [acting, setActing] = useState(false);
  const catMeta = CATEGORY_META[item.category];
  const type = item.type || 'initiative';
  const isOwner = user && item.author && user.id === item.author.id;
  const canDelete = isOwner || user?.is_admin;

  const toggle = async () => {
    if (joining) return;
    setJoining(true);
    try {
      const res = await api.post(`/initiatives/${item.id}/join`);
      onChanged && onChanged({ ...item, is_joined: res.data.joined, participant_count: res.data.participant_count });
    } catch { toast.error(t('errorOccurred')); }
    finally { setJoining(false); }
  };

  const del = async () => {
    if (acting || !window.confirm(t('confirmDelete'))) return;
    setActing(true);
    try {
      await api.delete(`/initiatives/${item.id}`);
      onDeleted && onDeleted(item.id);
      toast.success(t('delete'));
    } catch { toast.error(t('errorOccurred')); } finally { setActing(false); }
  };

  return (
    <Card className="p-4 rounded-2xl bg-card border border-border relative overflow-hidden" data-testid="bacheca-card">
      {catMeta && (<div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: catMeta.color }} />)}
      {item.image_base64 && (<div className="aspect-video rounded-xl overflow-hidden bg-secondary mb-3"><img src={item.image_base64} alt={item.title} className="w-full h-full object-cover" /></div>)}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <TypeBadge type={type} />
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${item.is_paid ? 'bg-[#FFEDD5] text-[#9A3412]' : 'bg-[#DCFCE7] text-[#166534]'}`}>{item.is_paid ? t('paid') : t('free')}</span>
          </div>
          <div className="font-display font-semibold text-base leading-tight" data-testid="bacheca-card-title">{item.title}</div>
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="bacheca-card-author">{item.author?.full_name} · {timeAgo(item.created_at, t)}</div>
        </div>
      </div>
      <p className="text-sm mt-2 whitespace-pre-wrap line-clamp-5">{item.description}</p>
      <MetaRow item={item} />
      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Users2 className="h-4 w-4" /> {item.participant_count || 0} {t('participants')}
        </div>
        <div className="flex gap-1">
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={del} disabled={acting} aria-label={t('delete')} className="text-muted-foreground hover:text-destructive" data-testid="bacheca-delete-button"><Trash2 className="h-4 w-4" /></Button>
          )}
          <ReportDialog targetType="board" targetId={item.id} />
          <BookmarkButton targetType="initiative" targetId={item.id} size="sm" />
          <Button variant={item.is_joined ? 'secondary' : 'default'} size="sm" onClick={toggle} disabled={joining} data-testid="bacheca-join-button">{item.is_joined ? t('leave') : t('join')}</Button>
        </div>
      </div>
    </Card>
  );
};
